import { Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { Toaster as RadixToaster } from "@/components/ui/toaster";
import { NavigationProvider } from "./components/navigation/NavigationContext";
import CookieConsent from "./components/cookies/CookieConsent";
import PlausibleAnalytics from "./components/analytics/PlausibleAnalytics";
import { LanguageProvider } from "./contexts/LanguageContext";
import MainNav from "./components/navigation/MainNav";
import NavLink from "./components/navigation/NavLink";
import SkipLink from "./components/ui/skip-link";
import ColorBlindFilters from "./components/ui/ColorBlindFilters";
import { useRealtimeSubscriptions } from "./hooks/useRealtimeSubscriptions";
import Index from "./pages/Index";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";

// Lazy-loaded routes (code-splitting pour réduire le bundle initial)
const Auth = lazy(() => import("./pages/Auth"));
const Landing = lazy(() => import("./pages/Landing"));
const MonetizationPage = lazy(() => import("./pages/Monetization"));
const ReferralDashboard = lazy(() => import("./pages/ReferralDashboard"));
const AdvertisingCampaign = lazy(() => import("./pages/AdvertisingCampaign"));
const PitchContest = lazy(() => import("./pages/PitchContest"));
const CollaborativeSpace = lazy(() => import("./pages/CollaborativeSpace"));
const PrestataireSpace = lazy(() => import("./pages/PrestataireSpace"));
const PressKit = lazy(() => import("./pages/PressKit"));
const LaunchLanding = lazy(() => import("./pages/LaunchLanding"));
const PrestataireTenders = lazy(() => import("./pages/PrestataireTenders"));
const ContactCollectivites = lazy(() => import("./pages/ContactCollectivites"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const MairieDashboard = lazy(() => import("./pages/MairieDashboard"));
const ApiDocs = lazy(() => import("./pages/ApiDocs"));
const Favorites = lazy(() => import("./pages/Favorites"));
const Impact = lazy(() => import("./pages/Impact"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const Achievements = lazy(() => import("./pages/Achievements"));
const Profile = lazy(() => import("./pages/Profile"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Community = lazy(() => import("./pages/Community"));
const Partners = lazy(() => import("./pages/Partners"));
const Welcome = lazy(() => import("./pages/Welcome"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const EmailUnsubscribe = lazy(() => import("./pages/EmailUnsubscribe"));
const LegalNotice = lazy(() => import("./pages/LegalNotice"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiesPolicy = lazy(() => import("./pages/CookiesPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

import "./components/ui/accessibility-styles.css";

const RouteFallback = () => (
  <div className="flex items-center justify-center min-h-[60vh]" role="status" aria-label="Chargement">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

const NotFound = () => (
  <div className="flex flex-col items-center justify-center min-h-[70vh] text-center px-4">
    <h1 className="text-4xl font-bold mb-4">404</h1>
    <p className="text-xl mb-8">Page non trouvée</p>
    <NavLink to="/" className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors">
      Retour à l'accueil
    </NavLink>
  </div>
);

function App() {
  useRealtimeSubscriptions();

  return (
    <LanguageProvider>
      <NavigationProvider>
        <div className="app">
          <ColorBlindFilters />
          <SkipLink targetId="main-content" />
          <Toaster richColors />
          <RadixToaster />
          <CookieConsent />
          <PlausibleAnalytics />
          <MainNav />
          <div className="pt-16" id="main-content">
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/landing" element={<Landing />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/api-docs" element={<ApiDocs />} />
                <Route path="/favorites" element={<Favorites />} />

                <Route path="/auth" element={<Auth />} />

                <Route path="/monetization" element={<ProtectedRoute><MonetizationPage /></ProtectedRoute>} />
                <Route path="/referral-dashboard" element={<ProtectedRoute><ReferralDashboard /></ProtectedRoute>} />
                <Route path="/advertising-campaign" element={<ProtectedRoute><AdvertisingCampaign /></ProtectedRoute>} />
                <Route path="/collaborative-space" element={<ProtectedRoute><CollaborativeSpace /></ProtectedRoute>} />
                <Route path="/prestataire-space" element={<ProtectedRoute><PrestataireSpace /></ProtectedRoute>} />
                <Route path="/prestataire-tenders" element={<ProtectedRoute><PrestataireTenders /></ProtectedRoute>} />

                <Route path="/mairie" element={<ProtectedRoute><MairieDashboard /></ProtectedRoute>} />

                <Route path="/contact-collectivites" element={<ContactCollectivites />} />
                <Route path="/pitch-contest" element={<PitchContest />} />
                <Route path="/press-kit" element={<PressKit />} />
                <Route path="/launch" element={<LaunchLanding />} />
                <Route path="/welcome" element={<Welcome />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/email-unsubscribe" element={<EmailUnsubscribe />} />

                <Route path="/legal" element={<LegalNotice />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/cookies" element={<CookiesPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />

                <Route path="/how-it-works" element={<Landing />} />
                <Route path="/commitments" element={<Landing />} />

                <Route path="/impact" element={<Impact />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/roadmap" element={<Roadmap />} />
                <Route path="/achievements" element={<Achievements />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/community" element={<Community />} />
                <Route path="/partners" element={<Partners />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </div>
        </div>
      </NavigationProvider>
    </LanguageProvider>
  );
}


export default App;
